/**
 * Session form (tutoring).
 */
import * as React from 'react';
import { FC } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Users } from 'lucide-react';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { AutoAssignmentProposalModal } from './AutoAssignmentProposalModal';
import { TutoringSessionAcademicSection } from './TutoringSessionAcademicSection';
import { TutoringSessionGeneralSection } from './TutoringSessionGeneralSection';
import { TutoringSessionGroupsSection } from './TutoringSessionGroupsSection';
import { TutoringSessionLocationSection } from './TutoringSessionLocationSection';
import { TutoringSessionParticipantsSection } from './TutoringSessionParticipantsSection';
import { TutoringSessionScheduleSection } from './TutoringSessionScheduleSection';
import { CustomDragOverlay } from '@/components/common/CustomDragOverlay';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import {
  ClassroomCapacityExceededError,
  EMPTY_SESSION_DATA,
  EMPTY_ACTIVITY_DRAFT,
  EMPTY_ACTIVITY_TYPE_DRAFT,
  ensureTutorAvailableForAdd,
  ensureStudentAvailableForAdd,
  formatStudentDisplayName,
  formatTutorDisplayName,
  getStudentAddBlockReason,
  getTutorAddBlockReason,
  type AddParticipantOutcome,
  useSessionTutorAvailability,
  useSessionStudentAvailability,
  sessionFormCollisionDetection,
  applySessionFieldSideEffects,
  resolveMaxTutors,
  resolveMaxStudents,
  createModeAttendance,
  countDtoParticipants,
  validateInlineActivityForms,
  buildInlineActivityPayload,
  getSelectedSessionEntities,
  editGroupToPrefill,
  attendanceForFormMode,
  addStudentBlockToast,
  addTutorBlockToast,
  addedParticipantMessage,
  alignSessionTypeForPlanification,
  applyCreateSessionMutationError,
  applyDefaultSubjectsIfEmpty,
  applyProposalResultMessage,
  autoAssignBlockMessage,
  buildAutoAssignRequest,
  estimateCreateSessionCount,
  ensureInPersonFormat,
  ensureStudentInSelection,
  fieldValueForValidation,
  formatQueuedCreateProgressLabel,
  getCreateSessionButtonLabel,
  heavyCreateSuffix,
  initialSessionType,
  invalidateTutoringSessionQueries,
  trackSessionsBulkJob,
  planificationTypeForMetadata,
  isRecurrencePreviewEnabled,
  isRecurringSessionSelection,
  isSessionSlotComplete,
  isTutorDragSource,
  isTutoringEditGroup,
  isTutoringSubmitDisabled,
  isTutoringSubmitLoading,
  maxStudentsTrimWarning,
  nextTypeResetArming,
  plannedSessionItemCount,
  queuedCreateProgressPercent,
  isSessionEditQueryEnabled,
  resolveAddParticipantBlock,
  resolveAvailabilityScope,
  resolveDragStartUser,
  resolveGroupStudentAfterAdd,
  resolveTutorLimitMessage,
  resolveSelectedEstablishmentForForm,
  resolveTutoringCollisionDetection,
  classroomCapacityAlertDescription,
  runTutoringDragEnd,
  runTutoringSubmit,
  sessionToEditPrefill,
  shouldCheckClassroomCapacityForCreate,
  shouldClearParticipantsOnTypeChange,
  shouldShowRecurrenceScope,
  shouldValidateCreateTypeField,
  shouldWrapTutoringDnd,
  showHeavyCreateWarning,
  showQueuedCreateBanner,
  showQueuedProgressBar,
  trimStudentsOverMax,
  tutoringCloseLabel,
  tutoringFormHeading,
  tutoringSubmitLabel,
  unmatchStudent,
  unmatchStudentFromTutor,
  unmatchStudentsOfTutor,
  withFacilitatorRole,
  withParticipantPresent,
  withStudentRole,
  withoutParticipantAttendance,
  getSessionDayOfWeek,
} from '@/components/sessions/inperson/session-form-utils';
import { useSessionMinDate } from '@/hooks/useSessionMinDate';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { resolveDefaultCreateSubjectIds } from '@/components/subjects/subject-form-utils';
import {
  checkClassroomCapacity,
  createSessionsBulkAsync,
  getSession,
  updateSession,
  previewRecurrence,
  updateSessionGroup,
} from '@/services/inperson/sessions.service';
import { proposeDraftAutoAssignment } from '@/services/inperson/matching.service';
import type { AutoAssignmentProposal } from '@/types/inperson/matching.types';
import {
  isGroupSessionType,
  type QueuedSessionsBulkJob,
  type ClassroomCapacityResult,
  type SessionBulkJob,
  type SessionGroup,
  type UpdateSessionGroupDto,
  type SessionType,
  type RecurrenceUpdateScope,
  type CreateSessionDto,
  type PlanificationType,
  type SessionFormData,
  type UpdateSessionDto,
} from '@/types/inperson/session.types';
import type { CreateActivityDto } from '@/types/activity.types';
import type { CreateActivityTypeDto } from '@/types/activity-type.types';
import type { UserWithProfile } from '@/types/user.types';
import type { ParticipantAttendanceById } from '@/types/attendance.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { HEAVY_CREATE_THRESHOLD } from '@/components/sessions/session-configs';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  sessionInPersonValidationSchema,
  activityConfigValidationSchema,
  activityTypeValidationSchema,
} from '@/utils/validation-schemas-utils';
import { getEstablishment } from '@/services/establishments.service';
import type { Establishment } from '@/types/establishment.types';

/** Props for {@link TutoringSessionEditGate}. */
interface TutoringSessionEditGateProps {
  mode: 'create' | 'edit';
  sessionLoading: boolean;
  hasSession: boolean;
  className?: string;
}

/** Spinner or missing-session gate while an edit form hydrates. */
function TutoringSessionEditGate({
  mode,
  sessionLoading,
  hasSession,
  className,
}: Readonly<TutoringSessionEditGateProps>): React.ReactElement | null {
  if (mode !== 'edit') return null;
  if (sessionLoading) {
    return (
      <div
        className={cn('collapse-replace-form h-full flex items-center justify-center', className)}
      >
        <Spinner />
      </div>
    );
  }
  if (hasSession) return null;
  return (
    <div className={cn('collapse-replace-form h-full flex items-center justify-center', className)}>
      <p className="text-muted-foreground">{getErrorMessage('sessionNotFound')}</p>
    </div>
  );
}

/** Props for {@link TutoringSessionForm}. */
interface TutoringSessionFormProps {
  mode: 'create' | 'edit';
  planificationType?: PlanificationType;
  editGroup?: SessionGroup;
  showParticipants?: boolean;
  sessionId?: string;
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
  onRequestCreateTutor?: () => void;
}

/** Session form (tutoring). */
export const TutoringSessionForm: FC<TutoringSessionFormProps> = ({
  mode,
  planificationType,
  editGroup,
  showParticipants = true,
  sessionId,
  onClose,
  onSuccess,
  className,
  onRequestCreateTutor,
}) => {
  const isEditGroup = isTutoringEditGroup(mode, editGroup);
  const queryClient = useQueryClient();

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['sessions', sessionId ?? ''],
    queryFn: () => getSession(sessionId!),
    enabled: isSessionEditQueryEnabled(mode, sessionId),
  });

  const [sessionData, setSessionData] = React.useState<SessionFormData>({
    ...EMPTY_SESSION_DATA,
    // In individual planification the only valid type is pre-set
    type: initialSessionType(planificationType),
  });
  const { minSessionDate } = useSessionMinDate(sessionData.establishmentId || null);

  const [selectedTutors, setSelectedTutors] = React.useState<UserWithProfile[]>([]);
  const [selectedStudents, setSelectedStudents] = React.useState<UserWithProfile[]>([]);
  const [studentToTutor, setStudentToTutor] = React.useState<Record<string, string>>({});

  const [proposalModalOpen, setProposalModalOpen] = React.useState(false);
  const [autoProposal, setAutoProposal] = React.useState<AutoAssignmentProposal | null>(null);

  const [recurrenceUpdateScope, setRecurrenceUpdateScope] =
    React.useState<RecurrenceUpdateScope>('occurrence');

  const [queuedJob, setQueuedJob] = React.useState<QueuedSessionsBulkJob | null>(null);
  const [creationProgress, setCreationProgress] = React.useState<SessionBulkJob | null>(null);
  const [capacityAlert, setCapacityAlert] = React.useState<ClassroomCapacityResult | null>(null);
  const [isCheckingSubmit, setIsCheckingSubmit] = React.useState(false);

  const [editAttendance, setEditAttendance] = React.useState<ParticipantAttendanceById>({});
  const createAttendance = React.useMemo<ParticipantAttendanceById>(
    () => createModeAttendance(mode, selectedTutors, selectedStudents),
    [mode, selectedTutors, selectedStudents],
  );
  const attendanceByUserId = attendanceForFormMode(mode, createAttendance, editAttendance);

  const isGroupMode = isGroupSessionType(sessionData.type);
  const isRecurringSeries = !!editGroup?.seriesId;

  const isRecurringSelected = isRecurringSessionSelection(sessionData);

  const selectedTutorIds = React.useMemo(() => selectedTutors.map((t) => t.id), [selectedTutors]);
  const selectedStudentIds = React.useMemo(
    () => selectedStudents.map((s) => s.id),
    [selectedStudents],
  );

  const sessionSlot = React.useMemo(
    () => ({
      sessionDate: sessionData.sessionDate,
      startTime: sessionData.startTime,
      endTime: sessionData.endTime,
    }),
    [sessionData.sessionDate, sessionData.startTime, sessionData.endTime],
  );

  const sessionAvailabilityDay = React.useMemo(
    () => getSessionDayOfWeek(sessionData.sessionDate),
    [sessionData.sessionDate],
  );

  const availabilityScope = resolveAvailabilityScope({
    mode,
    showParticipants,
    editGroupId: editGroup?.id,
    sessionId,
  });

  const tutorAvailability = useSessionTutorAvailability({
    tutors: selectedTutors,
    slot: sessionSlot,
    ...availabilityScope,
  });

  const studentAvailability = useSessionStudentAvailability({
    students: selectedStudents,
    slot: sessionSlot,
    ...availabilityScope,
  });

  const { data: recurrencePreview } = useQuery({
    queryKey: [
      'recurrence-preview',
      sessionData.schoolYearId || null,
      sessionData.establishmentId || null,
      sessionData.sessionDate,
      sessionData.recurrenceType,
      JSON.stringify(sessionData.recurrenceRule ?? null),
      sessionData.planDuringHolidays,
      selectedTutorIds.join(','),
    ],
    queryFn: () =>
      previewRecurrence({
        schoolYearId: sessionData.schoolYearId || undefined,
        establishmentId: sessionData.establishmentId || null,
        sessionDate: sessionData.sessionDate,
        recurrenceType: sessionData.recurrenceType,
        recurrenceRule: sessionData.recurrenceRule,
        planDuringHolidays: sessionData.planDuringHolidays,
        tutorIds: selectedTutorIds,
      }),
    enabled: isRecurrencePreviewEnabled({
      mode,
      isEditGroup,
      isRecurringSelected,
      sessionDate: sessionData.sessionDate,
    }),
  });

  const plannedItemCount = plannedSessionItemCount(isGroupMode, selectedTutors.length);
  const estimatedTotalSessions = estimateCreateSessionCount(
    isRecurringSelected,
    recurrencePreview?.count,
    plannedItemCount,
  );
  const isHeavyCreate = estimatedTotalSessions >= HEAVY_CREATE_THRESHOLD;

  const progressTotalSessions = queuedJob?.expectedSessions ?? 0;
  const progressCreatedSessions = creationProgress?.createdSessions ?? 0;

  const selectedEstablishmentId = sessionData.establishmentId;

  const { data: selectedEstablishmentDetail } = useQuery({
    queryKey: ['establishment', selectedEstablishmentId],
    queryFn: () => getEstablishment(selectedEstablishmentId),
    enabled: !!selectedEstablishmentId,
  });

  const selectedEstablishmentForForm = React.useMemo(
    (): Establishment | undefined =>
      resolveSelectedEstablishmentForForm({
        mode,
        selectedEstablishmentId,
        selectedEstablishmentDetail,
        createFallbackName: isEditGroup ? editGroup?.establishmentName : undefined,
        sessionEstablishment: session?.establishment,
        sessionEstablishmentName: session?.establishmentName,
      }),
    [
      mode,
      selectedEstablishmentId,
      selectedEstablishmentDetail,
      isEditGroup,
      editGroup?.establishmentName,
      session,
    ],
  );

  const formData = useSessionFormData();

  const [activeDragUser, setActiveDragUser] = React.useState<UserWithProfile | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const collisionDetectionFn = sessionFormCollisionDetection;

  const [showActivityForm, setShowActivityForm] = React.useState(false);
  const [showActivityTypeForm, setShowActivityTypeForm] = React.useState(false);
  const [newActivityData, setNewActivityData] =
    React.useState<Partial<CreateActivityDto>>(EMPTY_ACTIVITY_DRAFT);
  const [newActivityTypeData, setNewActivityTypeData] =
    React.useState<Partial<CreateActivityTypeDto>>(EMPTY_ACTIVITY_TYPE_DRAFT);

  const validation = useFormValidation(sessionInPersonValidationSchema);
  const activityValidation = useFormValidation(activityConfigValidationSchema);
  const activityTypeValidation = useFormValidation(activityTypeValidationSchema);

  const defaultSubjectsAppliedRef = React.useRef(false);
  // Armed once the type settles in edit-group mode to avoid wiping prefilled participants
  const typeResetArmedRef = React.useRef(mode === 'edit' || !editGroup);
  const tutorToChildIdRef = React.useRef<Record<string, string>>({});
  const soleChildIdRef = React.useRef<string | null>(null);
  const groupPrefilledRef = React.useRef(false);
  const prevSessionTypeForParticipantsRef = React.useRef<SessionType | '' | null>(null);

  const selectedActivity = formData.activities.find((a) => a.id === sessionData.activityId);

  const getMaxTutors = (): number | null =>
    resolveMaxTutors({
      mode,
      planificationType,
      isEditGroup,
      sessionType: sessionData.type,
      activityMaxTutors: selectedActivity?.maxTutors,
    });

  const getMaxStudents = React.useCallback(
    (): number | null =>
      resolveMaxStudents({
        isEditGroup,
        activityMaxStudents: selectedActivity?.maxStudents,
      }),
    [selectedActivity, isEditGroup],
  );

  const maxTutors = getMaxTutors();
  const maxStudents = getMaxStudents();

  const tutorLimitMessage = React.useCallback(
    (incomingName?: string): string =>
      resolveTutorLimitMessage({
        mode,
        sessionType: sessionData.type,
        planificationType,
        maxTutors,
        incomingName,
      }),
    [mode, planificationType, maxTutors, sessionData.type],
  );

  /** Bulk-create sessions and track the queued job until it settles. */
  const createMutation = useMutation({
    mutationFn: async (input: { dtos: CreateSessionDto[]; isGroupMode: boolean }) => {
      const { dtos } = input;
      const first = dtos[0];

      const participantsToAdd = countDtoParticipants(dtos);

      if (shouldCheckClassroomCapacityForCreate(first, participantsToAdd) && first) {
        const capacity = await checkClassroomCapacity({
          roomId: first.roomId!,
          participantsToAdd,
          sessionDate: first.sessionDate,
          startTime: first.startTime,
          endTime: first.endTime,
        });
        if (!capacity.available) {
          throw new ClassroomCapacityExceededError(capacity);
        }
      }

      return createSessionsBulkAsync(dtos);
    },
    onSuccess: (queued, { isGroupMode: groupMode }) => {
      setQueuedJob(queued);
      void trackSessionsBulkJob(queued, {
        queryClient,
        isGroupMode: groupMode,
        onProgress: setCreationProgress,
        onSettled: () => onSuccess?.(),
      });
    },
    onError: (error: unknown) => {
      const outcome = applyCreateSessionMutationError(error, setCapacityAlert);
      if (outcome.kind === 'timeout') {
        toast.warning(getErrorMessage('createMaybeStarted'), { duration: 8000 });
        return;
      }
      if (outcome.kind === 'error') {
        toast.error(getErrorMessage(error));
      }
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: async (payload: UpdateSessionGroupDto) => {
      if (!editGroup) throw new Error(getErrorMessage('noGroupToUpdate'));
      return updateSessionGroup(editGroup.id, payload);
    },
    onSuccess: async () => {
      await invalidateTutoringSessionQueries(queryClient);
      toast.success('Séance mère mise à jour avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateSessionDto) => updateSession(sessionId!, dto),
    onSuccess: async () => {
      await invalidateTutoringSessionQueries(queryClient);
      toast.success('Séance modifiée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const proposeMutation = useMutation({
    mutationFn: proposeDraftAutoAssignment,
    onSuccess: (data) => {
      setAutoProposal(data);
      setProposalModalOpen(true);
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err));
    },
  });

  const markParticipantPresent = React.useCallback((userId: string) => {
    setEditAttendance((prev) => withParticipantPresent(prev, userId));
  }, []);

  const clearParticipantAttendance = React.useCallback((userId: string) => {
    setEditAttendance((prev) => withoutParticipantAttendance(prev, userId));
  }, []);

  /** Hydrate the form from the loaded session. */
  React.useEffect(() => {
    if (mode !== 'edit' || !session) return;
    setRecurrenceUpdateScope('occurrence');
    const prefill = sessionToEditPrefill(session);
    setSessionData(prefill.formData);
    validation.validateInitialFormData(prefill.formData);
    setSelectedTutors(prefill.tutors);
    setSelectedStudents(prefill.students);
    setEditAttendance(prefill.attendance);
    setStudentToTutor(prefill.studentToTutor);
  }, [session]);

  /** One-shot hydrate from the session group being edited. */
  React.useEffect(() => {
    if (mode !== 'create' || !editGroup || groupPrefilledRef.current) return;
    groupPrefilledRef.current = true;
    const prefill = editGroupToPrefill(editGroup);
    setSessionData(prefill.formData);
    validation.validateInitialFormData(prefill.formData);
    setSelectedTutors(prefill.tutors);
    setSelectedStudents(prefill.students);
    setStudentToTutor(prefill.studentToTutor);
    tutorToChildIdRef.current = prefill.tutorToChildId;
    soleChildIdRef.current = prefill.soleChildId;
  }, [editGroup]);

  React.useEffect(() => {
    if (mode !== 'create') return;
    if (editGroup) return; // edit-group: handled by group prefill effect
    validation.validateInitialFormData(sessionData);
  }, []);

  React.useEffect(() => {
    if (mode !== 'create') return;
    if (isEditGroup || defaultSubjectsAppliedRef.current || formData.subjects.length === 0) return;
    const defaultIds = resolveDefaultCreateSubjectIds(formData.subjects);
    if (defaultIds.length === 0) return;
    defaultSubjectsAppliedRef.current = true;
    setSessionData((prev) => {
      const next = applyDefaultSubjectsIfEmpty(prev, defaultIds);
      if (!next) return prev;
      validation.validateField('subjectIds', defaultIds, next);
      validation.markAsTouched('subjectIds');
      return next;
    });
  }, [isEditGroup, formData.subjects]);

  React.useEffect(() => {
    if (mode !== 'create') return;
    setSessionData((prev) => alignSessionTypeForPlanification(prev, planificationType));
  }, [planificationType, mode]);

  React.useEffect(() => {
    if (mode !== 'create') return;
    setSessionData((prev) => ensureInPersonFormat(prev));
  }, [planificationType, mode]);

  React.useEffect(() => {
    if (mode !== 'create') return;
    if (!shouldValidateCreateTypeField(planificationType)) return;
    validation.validateField('type', sessionData.type, sessionData);
  }, [planificationType, sessionData.type, mode]);

  /** After edit-group prefill arms, wipe participants when the session type changes. */
  React.useEffect(() => {
    if (mode !== 'create') return;
    const next = nextTypeResetArming({
      armed: typeResetArmedRef.current,
      prevType: prevSessionTypeForParticipantsRef.current,
      nextType: sessionData.type,
    });
    typeResetArmedRef.current = next.armed;
    prevSessionTypeForParticipantsRef.current = next.prevType;
    if (next.wipeParticipants) {
      setSelectedTutors([]);
      setSelectedStudents([]);
      setStudentToTutor({});
    }
  }, [sessionData.type, mode]);

  React.useEffect(() => {
    if (mode !== 'create') return;
    setSelectedStudents((current) => {
      const newMaxStudents = getMaxStudents();
      const trimmed = trimStudentsOverMax(current, newMaxStudents);
      if (trimmed.removedCount > 0 && newMaxStudents !== null) {
        toast.warning(
          getErrorMessage(maxStudentsTrimWarning(trimmed.removedCount, newMaxStudents)),
        );
      }
      return trimmed.students;
    });
  }, [sessionData.type, sessionData.activityId, getMaxStudents, mode]);

  /** Patch one metadata field. Changing type in create mode clears participants. */
  const handleFieldChange = (
    field: keyof SessionFormData,
    value: string | string[] | boolean | Record<string, unknown> | null,
  ) => {
    if (shouldClearParticipantsOnTypeChange(mode, field, value, sessionData.type)) {
      setSelectedTutors([]);
      setSelectedStudents([]);
      setStudentToTutor({});
      setEditAttendance({});
    }
    setSessionData((prev) => {
      const newData = applySessionFieldSideEffects({ ...prev, [field]: value }, field);
      validation.validateField(field, fieldValueForValidation(value), newData);
      validation.markAsTouched(field);
      validation.markAsDirty(field);
      return newData;
    });
  };

  /** Mark a metadata field as touched for validation. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Toast a block or success from an add-participant check. */
  const applyAddParticipantOutcome = React.useCallback(
    (outcome: AddParticipantOutcome): boolean => {
      if (!outcome.ok) {
        toast[outcome.kind](getErrorMessage(outcome.message));
        return false;
      }
      if (outcome.message) toast.success(outcome.message);
      return true;
    },
    [],
  );

  const addTutorToSession = (userData: UserWithProfile) => {
    const payload = addTutorBlockToast(
      resolveAddParticipantBlock(userData.id, selectedTutorIds, maxTutors),
      `${userData.firstName} ${userData.lastName}`,
      tutorLimitMessage(`${userData.firstName} ${userData.lastName}`),
    );
    if (payload) {
      toast[payload.kind](getErrorMessage(payload.text));
      return;
    }
    setSelectedTutors((prev) => [...prev, withFacilitatorRole(userData)]);
    if (mode === 'edit') markParticipantPresent(userData.id);
  };

  const addStudentToSession = (userData: UserWithProfile) => {
    const payload = addStudentBlockToast(
      resolveAddParticipantBlock(userData.id, selectedStudentIds, maxStudents),
      `${userData.firstName} ${userData.lastName}`,
      maxStudents ?? 0,
    );
    if (payload) {
      toast[payload.kind](getErrorMessage(payload.text));
      return;
    }
    setSelectedStudents((prev) => [...prev, withStudentRole(userData)]);
    if (mode === 'edit') markParticipantPresent(userData.id);
  };

  const ensureTutorAvailable = React.useCallback(
    async (
      userData: UserWithProfile,
      onSyncChecksPassed?: () => void,
      successMessage?: string,
    ): Promise<boolean> => {
      const syncBlock = getTutorAddBlockReason({
        user: userData,
        slot: sessionSlot,
        selectedTutors,
        maxTutors,
        tutorLimitMessage,
      });
      if (syncBlock) return applyAddParticipantOutcome(syncBlock);
      onSyncChecksPassed?.();
      const name = formatTutorDisplayName(userData);
      const { dismiss } = toast.loading(getErrorMessage({ code: 'checkingAvailabilityOf', name }));
      const result = await ensureTutorAvailableForAdd({
        user: userData,
        slot: sessionSlot,
        excludeSessionGroupId: availabilityScope.excludeSessionGroupId,
        excludeSessionId: availabilityScope.excludeSessionId,
        successMessage,
      });
      dismiss();
      return applyAddParticipantOutcome(result);
    },
    [
      sessionSlot,
      selectedTutors,
      maxTutors,
      tutorLimitMessage,
      availabilityScope,
      applyAddParticipantOutcome,
    ],
  );

  const ensureStudentAvailable = React.useCallback(
    async (
      userData: UserWithProfile,
      onSyncChecksPassed?: () => void,
      successMessage?: string,
    ): Promise<boolean> => {
      const syncBlock = getStudentAddBlockReason({
        user: userData,
        slot: sessionSlot,
        selectedStudents,
        maxStudents,
      });
      if (syncBlock) return applyAddParticipantOutcome(syncBlock);
      onSyncChecksPassed?.();
      const name = formatStudentDisplayName(userData);
      const { dismiss } = toast.loading(getErrorMessage({ code: 'checkingAvailabilityOf', name }));
      const result = await ensureStudentAvailableForAdd({
        user: userData,
        slot: sessionSlot,
        excludeSessionGroupId: availabilityScope.excludeSessionGroupId,
        excludeSessionId: availabilityScope.excludeSessionId,
        successMessage,
      });
      dismiss();
      return applyAddParticipantOutcome(result);
    },
    [sessionSlot, selectedStudents, maxStudents, availabilityScope, applyAddParticipantOutcome],
  );

  /** Keep the dragged user for the overlay. */
  const handleDragStart = (event: DragStartEvent) => {
    const user = resolveDragStartUser(
      event.active.data.current?.user as UserWithProfile | undefined,
      isTutorDragSource(
        event.active.data.current?.roleGroup as string | undefined,
        event.active.data.current?.type as string | undefined,
      ),
      isSessionSlotComplete(sessionData),
    );
    if (user) setActiveDragUser(user);
  };

  /** Drop a tutor or student onto the zone / matching column. */
  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragUser(null);
    const dragOutcome = await runTutoringDragEnd(event, {
      isGroupMode,
      selectedStudents,
      selectedTutors,
      matchStudentToTutor,
      addTutorToSession,
      addStudentToSession,
      ensureTutorAvailable,
      ensureStudentAvailable,
      handleRemoveTutor,
      handleRemoveStudent,
    });
    if (dragOutcome.assignStudentToGroupHint) {
      toast.warning(getErrorMessage('assignStudentToGroup'));
    }
  };

  /** Drop a tutor and unmatch their students. */
  const handleRemoveTutor = (userId: string) => {
    setSelectedTutors((prev) => prev.filter((t) => t.id !== userId));
    if (mode === 'edit') clearParticipantAttendance(userId);
    setStudentToTutor((prev) => unmatchStudentsOfTutor(prev, userId));
  };

  /** Drop a student and clear their tutor match. */
  const handleRemoveStudent = (userId: string) => {
    setSelectedStudents((prev) => prev.filter((s) => s.id !== userId));
    if (mode === 'edit') clearParticipantAttendance(userId);
    setStudentToTutor((prev) => unmatchStudent(prev, userId));
  };

  /** Ensure the student is selected and pair them with the tutor. */
  const matchStudentToTutor = React.useCallback(
    (student: UserWithProfile, tutorId: string) => {
      setSelectedStudents((prev) => ensureStudentInSelection(prev, student));
      if (mode === 'edit') markParticipantPresent(student.id);
      setStudentToTutor((prev) => ({ ...prev, [student.id]: tutorId }));
    },
    [mode, markParticipantPresent],
  );

  /** Clear one student–tutor pair without removing the student. */
  const handleUnmatchStudent = React.useCallback((studentId: string, tutorId: string) => {
    setStudentToTutor((prev) => unmatchStudentFromTutor(prev, studentId, tutorId));
  }, []);

  /** Request an auto-assignment proposal for the current tutors/students. */
  const handleAutoAssign = React.useCallback(() => {
    const block = autoAssignBlockMessage({
      tutorCount: selectedTutors.length,
      studentCount: selectedStudents.length,
      establishmentId: sessionData.establishmentId,
      sessionType: sessionData.type,
    });
    if (block) {
      toast.warning(getErrorMessage(block));
      return;
    }
    proposeMutation.mutate(
      buildAutoAssignRequest({
        sessionType: sessionData.type as SessionType,
        establishmentId: sessionData.establishmentId,
        subjectId: sessionData.subjectIds?.[0] ?? null,
        activityId: sessionData.activityId,
        tutors: selectedTutors,
        students: selectedStudents,
      }),
    );
  }, [
    selectedTutors,
    selectedStudents,
    sessionData.establishmentId,
    sessionData.type,
    sessionData.subjectIds,
    sessionData.activityId,
    proposeMutation,
  ]);

  /** Apply the proposed `studentId → tutorId` map and close the modal. */
  const handleApplyProposal = React.useCallback(
    (mapping: Record<string, string>) => {
      setStudentToTutor(mapping);
      setProposalModalOpen(false);
      const result = applyProposalResultMessage(
        Object.keys(mapping).length,
        selectedStudents.length,
      );
      toast[result.kind](getErrorMessage(result.text));
    },
    [selectedStudents.length],
  );

  /** Availability-check then add the tutor from a double-click. */
  const handleTutorDoubleClick = React.useCallback(
    async (user: UserWithProfile) => {
      const ok = await ensureTutorAvailable(
        user,
        undefined,
        addedParticipantMessage(user, 'tutor'),
      );
      if (!ok) return;
      addTutorToSession(user);
    },
    [ensureTutorAvailable, addTutorToSession],
  );

  /** Availability-check then add the student from a double-click. */
  const handleStudentDoubleClick = React.useCallback(
    async (user: UserWithProfile) => {
      const ok = await ensureStudentAvailable(
        user,
        undefined,
        addedParticipantMessage(user, 'student'),
      );
      if (!ok) return;
      addStudentToSession(user);
      const groupAction = resolveGroupStudentAfterAdd(isGroupMode, selectedTutors.length);
      if (groupAction === 'match-single') {
        matchStudentToTutor(user, selectedTutors[0].id);
      }
      if (groupAction === 'warn-multi') {
        toast.warning(getErrorMessage('assignStudentToGroup'));
      }
    },
    [ensureStudentAvailable, addStudentToSession, isGroupMode, selectedTutors, matchStudentToTutor],
  );

  const validateActivityForms = (): boolean => {
    const result = validateInlineActivityForms({
      showActivityForm,
      showActivityTypeForm,
      newActivityData,
      newActivityTypeData,
    });
    if (!result.valid && result.message) toast.error(getErrorMessage(result.message));
    return result.valid;
  };

  const buildActivityData = () =>
    buildInlineActivityPayload({
      showActivityForm,
      showActivityTypeForm,
      activityId: sessionData.activityId,
      newActivityData,
      newActivityTypeData,
    });

  const getSelectedEntities = () =>
    getSelectedSessionEntities({
      establishmentId: sessionData.establishmentId,
      programId: sessionData.programId,
      activityId: sessionData.activityId,
      roomId: sessionData.roomId,
      establishments: formData.establishments,
      programs: formData.programs,
      activities: formData.activities,
      classrooms: formData.classrooms,
    });

  /** Validate, then POST create or PATCH edit (group vs child). */
  const handleSubmit = async () => {
    const submitError = await runTutoringSubmit({
      mode,
      queuedJob,
      createPending: createMutation.isPending,
      validation,
      sessionData,
      validateActivityForms,
      showParticipants,
      isGroupMode,
      selectedTutors,
      selectedStudents,
      studentToTutor,
      tutorAvailability,
      studentAvailability,
      setIsCheckingSubmit,
      buildActivityData,
      getSelectedEntities,
      selectedEstablishmentDetail,
      isEditGroup,
      isRecurringSeries,
      recurrenceUpdateScope,
      tutorToChildId: tutorToChildIdRef.current,
      soleChildId: soleChildIdRef.current,
      createMutation,
      updateGroupMutation,
      updateMutation,
      formData,
      session,
    });
    if (submitError) toast.error(getErrorMessage(submitError));
  };

  const editGate = TutoringSessionEditGate({
    mode,
    sessionLoading,
    hasSession: !!session,
    className,
  });
  if (editGate) return editGate;

  const showRecurrenceScope = shouldShowRecurrenceScope({
    mode,
    isEditGroup,
    isRecurringSeries,
    recurrenceGroupId: session?.recurrenceGroupId,
    sessionMode: session?.mode,
  });

  const createPending = createMutation.isPending;
  const updatePending = updateMutation.isPending;
  const updateGroupPending = updateGroupMutation.isPending;
  const { title: formTitle, subtitle: formSubtitle } = tutoringFormHeading({
    mode,
    isEditGroup,
    planificationType,
  });
  const showParticipantsArea = mode === 'edit' || showParticipants;
  const showClassicParticipants = showParticipantsArea && !isGroupMode;
  const showGroupParticipants = showParticipantsArea && isGroupMode;
  const slotIncomplete = !isSessionSlotComplete(sessionData);
  const isCreateDisabled = isTutoringSubmitDisabled({
    mode,
    isEditGroup,
    isFormValid: validation.isFormValid(),
    createPending,
    hasQueuedJob: queuedJob !== null,
    hasSubjects: sessionData.subjectIds.length > 0,
    updateGroupPending,
    updatePending,
  });
  const createButtonLabel = getCreateSessionButtonLabel(queuedJob !== null, createPending);
  const showQueuedBanner = showQueuedCreateBanner(mode, isEditGroup, queuedJob !== null);
  const showHeavyWarning = showHeavyCreateWarning({
    mode,
    isEditGroup,
    createPending,
    hasQueuedJob: queuedJob !== null,
    isHeavyCreate,
  });
  const closeLabel = tutoringCloseLabel(mode, queuedJob !== null);
  const submitLabel = tutoringSubmitLabel({
    mode,
    isEditGroup,
    saveLabel: 'Enregistrer',
    createLabel: createButtonLabel,
  });
  const submitLoading = isTutoringSubmitLoading({
    mode,
    createPending,
    updatePending,
    updateGroupPending,
    isCheckingSubmit,
    hasQueuedJob: queuedJob !== null,
  });
  const showProgressBar = showQueuedProgressBar(progressCreatedSessions, progressTotalSessions);
  const formType = mode === 'create' && !isEditGroup ? 'create' : 'edit';

  function renderParticipantCountHeader(): React.ReactNode {
    const count = selectedTutors.length + selectedStudents.length;
    if (count === 0) return null;
    return (
      <div className="section-header flex-shrink-0">
        <div className="flex items-center gap-2 text-xs text-[var(--color-2-nuance-1)]">
          <Users className="w-3.5 h-3.5" />
          <span>{count} participant(s)</span>
        </div>
      </div>
    );
  }

  function renderMetadataSections(): React.ReactNode {
    return (
      <>
        {renderParticipantCountHeader()}
        <TutoringSessionGeneralSection
          form={sessionData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          planificationType={planificationTypeForMetadata(mode, planificationType)}
          showRecurrenceScope={showRecurrenceScope}
          recurrenceUpdateScope={recurrenceUpdateScope}
          onRecurrenceScopeChange={setRecurrenceUpdateScope}
          recurrenceMode={mode}
        />
        <TutoringSessionLocationSection
          form={sessionData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          establishments={formData.establishments}
          classrooms={formData.classrooms}
          programs={formData.programs}
          activities={formData.activities}
          selectedEstablishment={selectedEstablishmentForForm}
          showActivityForm={showActivityForm}
          setShowActivityForm={setShowActivityForm}
          showActivityTypeForm={showActivityTypeForm}
          setShowActivityTypeForm={setShowActivityTypeForm}
          newActivityData={newActivityData}
          setNewActivityData={setNewActivityData}
          newActivityTypeData={newActivityTypeData}
          setNewActivityTypeData={setNewActivityTypeData}
          activityValidation={activityValidation}
          activityTypeValidation={activityTypeValidation}
        />
        <TutoringSessionScheduleSection
          form={sessionData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          disablePastDate={false}
          minDate={minSessionDate}
        />
        <TutoringSessionAcademicSection
          form={sessionData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          levels={formData.levels}
          subjects={formData.subjects}
          selectedEstablishment={selectedEstablishmentForForm}
        />
      </>
    );
  }

  function renderCreateBanners(): React.ReactNode {
    if (!showQueuedBanner && !showHeavyWarning) return null;
    return (
      <div className="w-full space-y-3 mb-3">
        {showQueuedBanner ? (
          <div className="flex items-start gap-3 rounded-md border border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_8%,transparent)] px-3 py-2 text-sm">
            <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-[var(--color-2-nuance-1)]" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">
                Création lancée
                {formatQueuedCreateProgressLabel(progressCreatedSessions, progressTotalSessions)}
              </p>
              <p className="text-muted-foreground">
                Vous pouvez fermer cette fenêtre : la création continue et vous serez informé dès
                qu'elle est terminée.
              </p>
              {showProgressBar ? (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-1-nuance-3)]">
                  <div
                    className="h-full rounded-full bg-[var(--color-2-nuance-1)] transition-all"
                    style={{
                      width: `${queuedCreateProgressPercent(progressCreatedSessions, progressTotalSessions)}%`,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {showHeavyWarning ? (
          <div className="flex items-start gap-3 rounded-md border border-[rgb(245,158,11)] bg-[color-mix(in_srgb,rgb(245,158,11)_8%,transparent)] px-3 py-2 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[rgb(245,158,11)]" />
            <p className="min-w-0 flex-1 text-foreground">
              <span className="font-medium">
                Environ {estimatedTotalSessions} séances seront créées
              </span>
              {heavyCreateSuffix(plannedItemCount, recurrencePreview)}{' '}
              <span className="text-muted-foreground">
                Vous pourrez continuer à naviguer pendant la création.
              </span>
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  const formContent = (
    <>
      <CollapseReplaceForm
        formType={formType}
        title={formTitle}
        subtitle={formSubtitle}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel={submitLabel}
        cancelLabel={closeLabel}
        isSubmitting={submitLoading}
        isValid={!isCreateDisabled}
        footerExtra={renderCreateBanners()}
        className={className}
      >
        {renderMetadataSections()}
        {showClassicParticipants ? (
          <TutoringSessionParticipantsSection
            selectedTutors={selectedTutors}
            selectedStudents={selectedStudents}
            selectedTutorIds={selectedTutorIds}
            selectedStudentIds={selectedStudentIds}
            maxTutors={maxTutors}
            maxStudents={maxStudents}
            onRemoveTutor={handleRemoveTutor}
            onRemoveStudent={handleRemoveStudent}
            onTutorDoubleClick={handleTutorDoubleClick}
            onStudentDoubleClick={handleStudentDoubleClick}
            onRequestCreateTutor={onRequestCreateTutor}
            establishmentId={sessionData.establishmentId || undefined}
            establishmentName={selectedEstablishmentDetail?.name}
            tutorAvailabilityById={tutorAvailability.availabilityById}
            tutorAvailabilityHint={tutorAvailability.softHint}
            tutorAvailabilityChecking={tutorAvailability.isChecking}
            studentAvailabilityById={studentAvailability.availabilityById}
            studentAvailabilityHint={studentAvailability.softHint}
            studentAvailabilityChecking={studentAvailability.isChecking}
            tutorDragDisabled={slotIncomplete}
            tutorDragDisabledTooltip={getErrorMessage('selectDateTimeBeforeParticipants')}
            studentDragDisabled={slotIncomplete}
            studentDragDisabledTooltip={getErrorMessage('selectDateTimeBeforeParticipants')}
            sessionAvailabilityDay={sessionAvailabilityDay}
            attendanceByUserId={attendanceByUserId}
          />
        ) : null}
        {showGroupParticipants ? (
          <TutoringSessionGroupsSection
            selectedTutors={selectedTutors}
            selectedStudents={selectedStudents}
            selectedTutorIds={selectedTutorIds}
            selectedStudentIds={selectedStudentIds}
            studentToTutor={studentToTutor}
            maxTutors={maxTutors}
            maxStudents={maxStudents}
            onRemoveTutor={handleRemoveTutor}
            onRemoveStudent={handleRemoveStudent}
            onUnmatchStudent={handleUnmatchStudent}
            onTutorDoubleClick={handleTutorDoubleClick}
            onStudentDoubleClick={handleStudentDoubleClick}
            onAutoAssign={handleAutoAssign}
            onRequestCreateTutor={onRequestCreateTutor}
            autoAssignPending={proposeMutation.isPending}
            establishmentId={sessionData.establishmentId || undefined}
            establishmentName={selectedEstablishmentDetail?.name}
            tutorAvailabilityById={tutorAvailability.availabilityById}
            tutorAvailabilityHint={tutorAvailability.softHint}
            tutorAvailabilityChecking={tutorAvailability.isChecking}
            studentAvailabilityById={studentAvailability.availabilityById}
            studentAvailabilityHint={studentAvailability.softHint}
            studentAvailabilityChecking={studentAvailability.isChecking}
            tutorDragDisabled={slotIncomplete}
            tutorDragDisabledTooltip={getErrorMessage('selectDateTimeBeforeParticipants')}
            studentDragDisabled={slotIncomplete}
            studentDragDisabledTooltip={getErrorMessage('selectDateTimeBeforeParticipants')}
            sessionAvailabilityDay={sessionAvailabilityDay}
            attendanceByUserId={attendanceByUserId}
          />
        ) : null}
      </CollapseReplaceForm>
      <AutoAssignmentProposalModal
        open={proposalModalOpen}
        onClose={() => setProposalModalOpen(false)}
        onApply={handleApplyProposal}
        proposal={autoProposal}
        tutors={selectedTutors}
        students={selectedStudents}
        isLoading={proposeMutation.isPending}
      />
      {capacityAlert ? (
        <ConfirmModal
          open={!!capacityAlert}
          onClose={() => setCapacityAlert(null)}
          onConfirm={() => setCapacityAlert(null)}
          title={getErrorMessage('classroomCapacityExceeded')}
          description={classroomCapacityAlertDescription(capacityAlert)}
          confirmText="J'ai compris"
          cancelText="Fermer"
          variant="warning"
        />
      ) : null}
    </>
  );

  const shouldWrapDnd = shouldWrapTutoringDnd(mode, showParticipants);
  if (!shouldWrapDnd) return formContent;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={resolveTutoringCollisionDetection(
        mode,
        isGroupMode,
        collisionDetectionFn,
        closestCenter,
      )}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {formContent}
      <CustomDragOverlay activeUser={activeDragUser} />
    </DndContext>
  );
};
